<script setup lang="ts">
/**
 * The front page.
 *
 * It fills the sheet. A cover that occupies the top fifth of a page and leaves
 * the rest white is not a cover, it is a heading, and the whole point of
 * drawing paper is to see that the first thing somebody is handed looks
 * finished. So the block is given the height the margins leave it and every
 * arrangement below decides what to do with that room.
 *
 * One set of parts, six arrangements. The markup is the same in every one and
 * only the stylesheet differs, which keeps a variant a layout rather than a
 * sixth component with its own bugs. The parts, in order: a logo, a
 * photograph, the standard line, the entity, a subtitle, a rule, the period,
 * the facts the document states about itself, and a foot note.
 *
 * The facts are not typed here. The framework, the assurance and the currency
 * are settings, because each is printed in more than one place and the places
 * have to agree; the cover reads them rather than carrying its own copy.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import { esgFrameworkName, frameworkName, pageBodyHeight } from '@/domain/layout'
import type { CoverSection } from '@/domain/section'
import { usesLogo, usesPicture } from '@/sections/cover/variants'
import { useReportStore } from '@/stores/report'

const { t } = useI18n()

const props = defineProps<{ section: CoverSection }>()

const report = useReportStore()

/** The paper less what the margins take, so the cover fills the sheet. */
const height = computed(() => pageBodyHeight(report.layout))

const takesPicture = computed(() => usesPicture(props.section.variant))
const takesLogo = computed(() => usesLogo(props.section.variant))

const showPicture = computed(() => takesPicture.value && props.section.picture !== '')
const showLogo = computed(() => takesLogo.value && props.section.logo !== '')

/**
 * Whether this arrangement carries a title block at all.
 *
 * `full` is a picture and nothing else: the design was finished elsewhere, so
 * drawing the standard, entity or period over it would put unwanted text on
 * top of somebody else's composition.
 */
const hasBlock = computed(() => props.section.variant !== 'full')

/**
 * What each line says, and whether it is the author's or a sample.
 *
 * An empty cover used to draw a title and nothing else, which made choosing
 * between six arrangements a matter of comparing six versions of one line.
 * So every empty field shows a sample of what belongs there instead.
 *
 * A sample is marked as one: it is drawn faint, it is never printed, and it
 * disappears the moment anything is typed. The risk being avoided is somebody
 * exporting a report with the word "Subtitle" on the cover.
 */
interface Line {
  readonly text: string
  readonly sample: boolean
}

function line(typed: string, sample: string): Line {
  const written = typed.trim()
  return written === '' ? { text: sample, sample: true } : { text: written, sample: false }
}

/**
 * The line under the title.
 *
 * What the author typed, or the accounting framework the document is set to
 * if they typed nothing. A cover that silently disagrees with the settings is
 * worse than one that says nothing, so the typed line always wins, and the
 * framework counts as real rather than as a sample: it *is* set.
 */
const standard = computed<Line>(() => {
  const typed = props.section.standard.trim()
  if (typed !== '') return { text: typed, sample: false }

  const framework = frameworkName(report.layout.framework)
  if (framework !== '') return { text: framework, sample: false }

  return { text: t('canvas.defaults.standard'), sample: true }
})

const entity = computed<Line>(() => line(props.section.entity, t('canvas.defaults.entity')))
const subtitle = computed<Line>(() => line(props.section.subtitle, t('canvas.defaults.subtitle')))
const period = computed<Line>(() => line(props.section.period, t('canvas.defaults.period')))
const note = computed<Line>(() => line(props.section.note, t('canvas.defaults.note')))

/**
 * The facts a cover shows before any have been set.
 *
 * Two samples, so the arrangements that give the facts their own band are
 * legible before somebody has chosen a framework. Replaced entirely the
 * moment one real fact exists.
 */
const sampleFacts = computed(() => [
  { key: 'framework', label: t('document.framework'), value: t('canvas.defaults.framework') },
  { key: 'assurance', label: t('document.assurance'), value: t('canvas.defaults.assurance') },
])

/**
 * What the document states about itself, printed as a short list.
 *
 * Only what has actually been set. A cover carrying "Assurance: none" tells a
 * reader something untrue about how much work went into it.
 */
const facts = computed(() => {
  const found: { key: string; label: string; value: string }[] = []
  const { framework, esgFramework, assurance } = report.layout

  // The accounting framework only appears here when the line above is being
  // used for something else, so it is never printed twice.
  if (framework !== 'none' && standard.value.text !== frameworkName(framework)) {
    found.push({
      key: 'framework',
      label: t('document.framework'),
      value: frameworkName(framework),
    })
  }

  if (esgFramework !== 'none') {
    found.push({
      key: 'esg',
      label: t('document.esgFramework'),
      value: esgFrameworkName(esgFramework),
    })
  }

  if (assurance !== 'none') {
    found.push({
      key: 'assurance',
      label: t('document.assurance'),
      value: t(`document.assurances.${assurance}`),
    })
  }

  return found
})
</script>

<template>
  <div class="cover" :class="`cover--${section.variant}`" :style="{ '--cover-height': height }">
    <span class="cover__wash" aria-hidden="true" />

    <!-- The picture, or the space it would take. Drawn empty rather than
         collapsed, so an arrangement built around a photograph still reads as
         one before a photograph has been chosen. -->
    <figure v-if="takesPicture" class="cover__picture" :class="{ 'is-sample': !showPicture }">
      <img v-if="showPicture" :src="section.picture" alt="" />
      <span v-else class="cover__picture-mark" aria-hidden="true">
        <svg width="34" height="34" viewBox="0 0 24 24">
          <rect
            x="3"
            y="4.5"
            width="18"
            height="15"
            rx="2.5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.2"
          />
          <circle cx="8.6" cy="10" r="1.6" fill="none" stroke="currentColor" stroke-width="1.2" />
          <path
            d="M3.4 17.2 8.8 12l3.4 3.2 3.2-2.8 5.2 4.6"
            fill="none"
            stroke="currentColor"
            stroke-width="1.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </figure>

    <img v-if="showLogo" class="cover__logo" :src="section.logo" alt="" />
    <span v-else-if="takesLogo" class="cover__logo cover__logo--sample" aria-hidden="true">
      {{ t('canvas.defaults.logo') }}
    </span>

    <div v-if="hasBlock" class="cover__block">
      <p class="cover__standard" :class="{ 'is-sample': standard.sample }">{{ standard.text }}</p>
      <h1 class="cover__entity" :class="{ 'is-sample': entity.sample }">{{ entity.text }}</h1>
      <p class="cover__subtitle" :class="{ 'is-sample': subtitle.sample }">{{ subtitle.text }}</p>
      <hr class="cover__rule" />
      <p class="cover__period" :class="{ 'is-sample': period.sample }">{{ period.text }}</p>

      <dl class="cover__facts" :class="{ 'is-sample': facts.length === 0 }">
        <div
          v-for="fact in facts.length > 0 ? facts : sampleFacts"
          :key="fact.key"
          class="cover__fact"
        >
          <dt>{{ fact.label }}</dt>
          <dd>{{ fact.value }}</dd>
        </div>
      </dl>
    </div>

    <p v-if="hasBlock" class="cover__note" :class="{ 'is-sample': note.sample }">
      {{ note.text }}
    </p>
  </div>
</template>

<style scoped>
/* ---------- What every arrangement shares ---------- */

.cover {
  position: relative;
  display: flex;
  flex-direction: column;
  /* The sheet, less its margins. This is what makes it a cover. */
  min-height: var(--cover-height, 240mm);
  /* The chrome the canvas puts around a block would otherwise show through
     the colour a variant paints to the edge. */
  overflow: hidden;
}

.cover__wash {
  display: none;
}

/* A sample: what belongs in a line, shown until somebody writes the line.
   Faint enough to read as a prompt rather than as content, and gone from
   anything printed or exported, because a report that went out with the word
   "Subtitle" on its cover would be this feature doing harm. */
.cover .is-sample {
  opacity: 0.34;
}

@media print {
  .cover .is-sample {
    visibility: hidden;
  }
}

.cover__logo--sample {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46mm;
  height: 16mm;
  border: 1px dashed var(--paper-rule);
  border-radius: 1.5mm;
  color: var(--paper-ink-soft);
  font-size: 8pt;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.6;
}

@media print {
  .cover__logo--sample {
    visibility: hidden;
  }
}

.cover__picture.is-sample {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--paper-rule);
  color: var(--paper-ink-soft);
  opacity: 0.5;
}

@media print {
  .cover__picture.is-sample {
    visibility: hidden;
  }
}

.cover__block {
  position: relative;
  z-index: 1;
}

.cover__standard {
  margin: 0 0 6mm;
  color: var(--page-accent);
  font-size: 9pt;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.cover__entity {
  margin: 0;
  font-size: 32pt;
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: -0.015em;
  /* A long company name must not be justified: a title page with rivers in
     it is the first thing a reader sees. */
  text-align: inherit;
}

.cover__subtitle {
  margin: 3mm 0 0;
  color: var(--paper-ink-soft);
  font-size: 13pt;
}

.cover__rule {
  height: 0;
  margin: 9mm 0 4mm;
  border: 0;
  border-top: 1.5px solid var(--page-accent);
}

.cover__period {
  margin: 0;
  font-size: 11pt;
}

/* What the document says about itself: label above value, in a row, small.
   A list rather than a paragraph, because these are read by looking for one
   of them rather than by reading through. */
.cover__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 4mm 10mm;
  margin: 10mm 0 0;
}

/* Each pair is wrapped, so the label sits above its own value rather than
   above whichever value the flex wrap happened to put under it. */
.cover__fact {
  min-width: 0;
}

.cover__facts dt {
  color: var(--paper-ink-soft);
  font-size: 7.5pt;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.cover__facts dd {
  margin: 1mm 0 0;
  font-size: 10pt;
}

.cover__logo {
  height: 16mm;
  width: auto;
  max-width: 60mm;
  object-fit: contain;
  object-position: left center;
}

.cover__picture {
  margin: 0;
  overflow: hidden;
}

.cover__picture img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.cover__note {
  margin: 0;
  color: var(--paper-ink-soft);
  font-size: 8pt;
  line-height: 1.5;
}

/* ---------- Classic: ranged left, the block in the upper third ---------- */

.cover--classic {
  justify-content: flex-start;
  text-align: left;
}

.cover--classic .cover__logo {
  margin-bottom: 24mm;
}

.cover--classic .cover__block {
  padding-top: 6mm;
}

.cover--classic .cover__note {
  margin-top: auto;
}

/* ---------- Centred: an annual report ---------- */

.cover--centred {
  align-items: center;
  justify-content: center;
  text-align: center;
}

.cover--centred .cover__logo {
  object-position: center;
  margin-bottom: 26mm;
}

.cover--centred .cover__block {
  max-width: 130mm;
}

.cover--centred .cover__rule {
  width: 34mm;
  margin-left: auto;
  margin-right: auto;
  margin-top: 10mm;
  margin-bottom: 6mm;
}

.cover--centred .cover__facts {
  justify-content: center;
  text-align: center;
}

.cover--centred .cover__note {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  text-align: center;
}

/* ---------- Band: the house colour across the head ---------- */

.cover--band {
  justify-content: flex-start;
  text-align: left;
  /* The band is drawn past the page's own margin, so it meets the trim the
     way a printed cover does. */
  margin: calc(var(--page-margin-top) * -1) calc(var(--page-margin-side, 20mm) * -1) 0;
  padding: var(--page-margin-top) var(--page-margin-side, 20mm) 0;
}

.cover--band .cover__wash {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 46%;
  background: var(--page-accent);
}

.cover--band .cover__logo {
  position: relative;
  z-index: 1;
  margin-bottom: 18mm;
  /* A wordmark on the band is nearly always the reversed one. */
  filter: brightness(0) invert(1);
}

.cover--band .cover__block {
  padding-bottom: 10mm;
}

.cover--band .cover__standard,
.cover--band .cover__entity,
.cover--band .cover__subtitle,
.cover--band .cover__period {
  color: #ffffff;
}

.cover--band .cover__subtitle,
.cover--band .cover__standard {
  opacity: 0.82;
}

.cover--band .cover__rule {
  border-top-color: rgba(255, 255, 255, 0.55);
}

.cover--band .cover__facts {
  margin-top: 26mm;
}

.cover--band .cover__note {
  margin-top: auto;
  padding-bottom: 2mm;
}

/* ---------- Portrait: a photograph across the upper half ---------- */

.cover--portrait {
  justify-content: flex-start;
  text-align: left;
  margin: calc(var(--page-margin-top) * -1) calc(var(--page-margin-side, 20mm) * -1) 0;
  padding: 0 var(--page-margin-side, 20mm) 0;
}

.cover--portrait .cover__picture {
  height: 45%;
  margin: 0 calc(var(--page-margin-side, 20mm) * -1) 14mm;
}

.cover--portrait .cover__logo {
  margin-bottom: 12mm;
}

.cover--portrait .cover__note {
  margin-top: auto;
  padding-bottom: var(--page-margin-bottom);
}

/* ---------- Framed: a rule around the sheet, the block centred ---------- */

.cover--framed {
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 14mm;
}

.cover--framed .cover__wash {
  display: block;
  position: absolute;
  inset: 0;
  border: 1px solid var(--page-accent);
  /* A second, hairline rule inside the first. Two lines a couple of
     millimetres apart is the oldest way of saying "this is the official
     copy", and it costs nothing to print. */
  box-shadow:
    inset 0 0 0 2.4mm var(--paper),
    inset 0 0 0 calc(2.4mm + 0.5px) var(--page-accent);
}

.cover--framed .cover__logo {
  object-position: center;
  margin-bottom: 20mm;
}

.cover--framed .cover__block {
  max-width: 120mm;
}

.cover--framed .cover__entity {
  font-size: 26pt;
}

.cover--framed .cover__rule {
  width: 28mm;
  margin: 8mm auto 5mm;
}

.cover--framed .cover__facts {
  justify-content: center;
}

.cover--framed .cover__note {
  position: absolute;
  bottom: 10mm;
  left: 14mm;
  right: 14mm;
  text-align: center;
}

/* ---------- Sidebar: a colour column down the leading edge ---------- */

.cover--sidebar {
  justify-content: flex-start;
  text-align: left;
  margin: calc(var(--page-margin-top) * -1) calc(var(--page-margin-side, 20mm) * -1) 0;
  padding: var(--page-margin-top) var(--page-margin-side, 20mm) 0
    calc(var(--page-margin-side, 20mm) + 34mm);
}

.cover--sidebar .cover__wash {
  display: block;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 26mm;
  background: var(--page-accent);
}

/* The photograph, where there is one, sits under the block rather than over
   it: the column already carries the colour, and two strong elements at the
   head of a page fight. */
.cover--sidebar .cover__picture {
  order: 3;
  height: 38%;
  margin: 14mm calc(var(--page-margin-side, 20mm) * -1) 0 0;
}

.cover--sidebar .cover__logo {
  margin-bottom: 22mm;
}

.cover--sidebar .cover__note {
  order: 4;
  margin-top: auto;
  padding-bottom: var(--page-margin-bottom);
}

/* ---------- Full: one picture, bled to every edge ---------- */

.cover--full {
  /* Only the top and sides pull negative, as every other bled arrangement
     does. A negative *bottom* margin would also drag whatever comes after
     the cover up into it: the box would still end where the margin used to
     start, but the section that follows in the flow would move too. Reaching
     the true bottom edge instead comes from adding both margins back onto
     the height, so the box grows downward rather than the boundary between
     sections moving. */
  min-height: calc(
    var(--cover-height, 240mm) + var(--page-margin-top, 0mm) + var(--page-margin-bottom, 0mm)
  );
  margin: calc(var(--page-margin-top) * -1) calc(var(--page-margin-side, 20mm) * -1) 0;
}

.cover--full .cover__picture {
  flex: 1;
  margin: 0;
}

/* ---------- Print ---------- */

@media print {
  .cover {
    /* Whatever follows starts on its own sheet. A cover with the first
       paragraph of the report under it is not a cover. */
    break-after: page;
  }

  .cover--band .cover__wash,
  .cover--sidebar .cover__wash,
  .cover--framed .cover__wash {
    /* Backgrounds are dropped by default when printing, which would take the
       band and the column away and leave white text on white paper. */
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
}
</style>
